import { useRef, type KeyboardEvent } from 'react';
import { cx } from './cx';

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: string;
}

/**
 * Segmented control: a radiogroup with a sliding active pill. Columns are
 * equal width so the pill can slide by whole-segment steps. Arrow keys move
 * the selection, as the radio pattern expects.
 */
export function Segmented<V extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  label,
  className,
  block,
}: {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (v: V) => void;
  /** lg = 40px auth tabs, md = 32px toolbar, sm = 30px. */
  size?: 'sm' | 'md' | 'lg';
  label: string;
  className?: string;
  block?: boolean;
}) {
  const idx = Math.max(0, options.findIndex((o) => o.value === value));
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const pad = size === 'lg' ? 4 : 3;
  const n = options.length;

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % n;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(
        'relative grid bg-seg',
        block ? 'w-full' : 'w-max max-w-full',
        size === 'lg' ? 'rounded-[15px]' : 'rounded-[12px]',
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, padding: pad }}
    >
      <span
        aria-hidden="true"
        className={cx('absolute bg-seg-on shadow-seg', size === 'lg' ? 'rounded-[11px]' : 'rounded-[9px]')}
        style={{
          top: pad,
          bottom: pad,
          left: pad,
          width: `calc((100% - ${pad * 2}px) / ${n})`,
          transform: `translateX(${idx * 100}%)`,
          transition: 'transform .45s var(--ease)',
        }}
      />
      {options.map((o, i) => {
        const on = i === idx;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={onKey}
            className={cx(
              'relative z-[1] bg-transparent border-0 cursor-pointer font-medium whitespace-nowrap rounded-[9px]',
              size === 'lg' ? 'h-10 text-[14px] px-3' : size === 'md' ? 'h-8 text-[13px] px-3' : 'h-[30px] text-[12.5px] px-2.5',
              on ? 'text-t1' : 'text-t4 hover:text-t2',
            )}
            style={{ transition: 'color .3s ease' }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
